import React, { useEffect, useState } from 'react';

export interface AppSuggestion {
    suggestionId: string;
    userId: string;
    createdAt: string;
    appName: string;
    description: string;
    website?: string;
    status?: string;
}

interface SuggestionGroup {
    normalizedName: string;
    displayName: string;
    count: number;
    suggestions: AppSuggestion[];
}

export type AppSuggestionsSectionProps = {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
};

const STATUS_OPTIONS = ['Pending', 'Under Review', 'Planned', 'Declined'] as const;
const STATUS_COLORS: Record<string, string> = {
    'Pending':      '#f59e0b',
    'Under Review': '#3b82f6',
    'Planned':      '#10b981',
    'Declined':     '#ef4444',
};

const normalize = (name: string) => name.toLowerCase().replace(/\s+/g, '');

function groupSuggestions(suggestions: AppSuggestion[]): SuggestionGroup[] {
    const map = new Map<string, SuggestionGroup>();
    for (const s of suggestions) {
        const key = normalize(s.appName);
        if (!map.has(key)) {
            map.set(key, { normalizedName: key, displayName: s.appName, count: 0, suggestions: [] });
        }
        const group = map.get(key)!;
        group.count++;
        group.suggestions.push(s);
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

export const AppSuggestionsSection: React.FC<AppSuggestionsSectionProps> = ({ API_BASE, authHeaders }) => {
    const [suggestions, setSuggestions] = useState<AppSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [groupStatuses, setGroupStatuses] = useState<Record<string, string>>({});

    const fetchSuggestions = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/app-suggestion`, {
                method: 'GET',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error((err as any)?.message || 'Failed to fetch suggestions.');
            }
            const data = await res.json();
            setSuggestions(Array.isArray(data.items) ? data.items : []);
        } catch (err: any) {
            setError(err.message || 'Error retrieving suggestions.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSuggestions(); }, []);

    const toggleExpand = (key: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const handleUpdateGroupStatus = async (group: SuggestionGroup) => {
        const status = groupStatuses[group.normalizedName] || STATUS_OPTIONS[0];
        setActionLoading(true);
        setActionError(null);
        try {
            const updates = group.suggestions.map(s => ({ suggestionId: s.suggestionId, status }));
            const res = await fetch(`${API_BASE}/app-suggestion`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(updates),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error((err as any)?.message || 'Failed to update status.');
            }
            await fetchSuggestions();
        } catch (err: any) {
            setActionError(err.message || 'Failed to update status.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteGroup = async (group: SuggestionGroup) => {
        if (!window.confirm(`Delete all ${group.count} suggestion(s) for "${group.displayName}"?`)) return;
        setActionLoading(true);
        setActionError(null);
        try {
            const deletes = group.suggestions.map(s => ({ suggestionId: s.suggestionId }));
            const res = await fetch(`${API_BASE}/app-suggestion`, {
                method: 'DELETE',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(deletes),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error((err as any)?.message || 'Failed to delete suggestions.');
            }
            await fetchSuggestions();
        } catch (err: any) {
            setActionError(err.message || 'Failed to delete suggestions.');
        } finally {
            setActionLoading(false);
        }
    };

    const groups = groupSuggestions(suggestions);

    const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', background: '#f3f4f6', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' };

    return (
        <section className="table-section" style={{ maxWidth: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>App Integration Suggestions</h2>
                <button
                    onClick={fetchSuggestions}
                    disabled={loading}
                    style={{ padding: '4px 12px', fontSize: 12, cursor: 'pointer', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff' }}
                >
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
                {suggestions.length > 0 && (
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                        {groups.length} unique platform{groups.length !== 1 ? 's' : ''} · {suggestions.length} total suggestion{suggestions.length !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {error && <p style={{ color: '#ef4444', margin: '0 0 12px' }}>{error}</p>}
            {actionError && <p style={{ color: '#ef4444', margin: '0 0 12px' }}>{actionError}</p>}

            {groups.length === 0 && !loading && (
                <p style={{ color: '#9ca3af', fontSize: 13 }}>No suggestions yet.</p>
            )}

            {groups.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th style={th}></th>
                                <th style={th}>Platform</th>
                                <th style={th}>Votes</th>
                                <th style={th}>Current Status</th>
                                <th style={th}>Update Status</th>
                                <th style={th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map(group => {
                                const isExpanded = expandedGroups.has(group.normalizedName);
                                const statuses = group.suggestions.map(s => s.status || 'Pending');
                                const mostCommonStatus = STATUS_OPTIONS.find(s => statuses.includes(s)) || 'Pending';
                                const selectedStatus = groupStatuses[group.normalizedName] || mostCommonStatus;
                                const statusColor = STATUS_COLORS[mostCommonStatus] || '#9ca3af';

                                return (
                                    <React.Fragment key={group.normalizedName}>
                                        <tr style={{ background: isExpanded ? '#f9fafb' : '#fff' }}>
                                            <td style={{ ...td, width: 32 }}>
                                                <button
                                                    onClick={() => toggleExpand(group.normalizedName)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#6b7280', padding: 0 }}
                                                    title={isExpanded ? 'Collapse' : 'Expand submissions'}
                                                >
                                                    {isExpanded ? '▼' : '▶'}
                                                </button>
                                            </td>
                                            <td style={{ ...td, fontWeight: 600 }}>{group.displayName}</td>
                                            <td style={td}>
                                                <span style={{ display: 'inline-block', background: '#4f46e5', color: '#fff', borderRadius: 12, padding: '2px 10px', fontWeight: 700, fontSize: 12 }}>
                                                    {group.count}
                                                </span>
                                            </td>
                                            <td style={td}>
                                                <span style={{ display: 'inline-block', background: statusColor + '22', color: statusColor, borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 12 }}>
                                                    {mostCommonStatus}
                                                </span>
                                            </td>
                                            <td style={{ ...td, display: 'flex', gap: 6, alignItems: 'center' }}>
                                                <select
                                                    value={selectedStatus}
                                                    onChange={e => setGroupStatuses(prev => ({ ...prev, [group.normalizedName]: e.target.value }))}
                                                    style={{ fontSize: 12, padding: '3px 6px', borderRadius: 4, border: '1px solid #d1d5db' }}
                                                >
                                                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                <button
                                                    onClick={() => handleUpdateGroupStatus(group)}
                                                    disabled={actionLoading}
                                                    style={{ padding: '3px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4, border: 'none', background: '#4f46e5', color: '#fff' }}
                                                >
                                                    Apply
                                                </button>
                                            </td>
                                            <td style={td}>
                                                <button
                                                    onClick={() => handleDeleteGroup(group)}
                                                    disabled={actionLoading}
                                                    style={{ padding: '3px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4, border: 'none', background: '#ef4444', color: '#fff' }}
                                                >
                                                    Delete all
                                                </button>
                                            </td>
                                        </tr>

                                        {isExpanded && group.suggestions
                                            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                                            .map(s => (
                                                <tr key={s.suggestionId} style={{ background: '#f9fafb' }}>
                                                    <td style={td} />
                                                    <td style={{ ...td, paddingLeft: 24, color: '#6b7280', fontStyle: 'italic' }}>{s.appName}</td>
                                                    <td style={{ ...td, color: '#9ca3af', fontSize: 12 }} colSpan={2}>
                                                        {new Date(s.createdAt).toLocaleDateString()} · {s.userId}
                                                    </td>
                                                    <td style={{ ...td, color: '#6b7280', fontSize: 12 }} colSpan={2}>{s.description}</td>
                                                </tr>
                                            ))
                                        }
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};
